// Shared types for the Sale Finder feature, used by both the Pages Functions
// (server) and the React app (client).

export const SALE_CATEGORIES = [
  'handbags',
  'makeup',
  'skincare',
  'perfume',
  'fashion',
] as const;

export const LUXURY_TIERS = ['accessible_premium', 'prestige', 'high_luxury'] as const;

export const SALE_STATUSES = [
  'LIVE',
  'NO_ACTIVE_SALE',
  'OUT_OF_STOCK',
  'EXPIRED',
  'UNVERIFIED',
  'SCAN_FAILED',
] as const;

export const PRODUCT_AVAILABILITY = ['in_stock', 'out_of_stock', 'unknown'] as const;

export type SaleCategory = (typeof SALE_CATEGORIES)[number];
export type LuxuryTier = (typeof LUXURY_TIERS)[number];
export type SaleStatus = (typeof SALE_STATUSES)[number];
export type ProductAvailability = (typeof PRODUCT_AVAILABILITY)[number];

export const LUXURY_TIER_LABELS: Record<LuxuryTier, string> = {
  accessible_premium: 'Accessible premium',
  prestige: 'Prestige',
  high_luxury: 'High luxury',
};

export interface Brand {
  id: string;
  name: string;
  slug: string;
  categories: SaleCategory[];
  luxuryTier: LuxuryTier;
  enabled: boolean;
}

export interface SaleProductExample {
  productName: string;
  productUrl: string | null;
  originalPriceMinor: number | null;
  salePriceMinor: number | null;
  currency: string | null;
  availability: ProductAvailability;
  discountPercent: number | null;
}

export interface Sale {
  id: string;
  brandId: string;
  brandName: string;
  luxuryTier: LuxuryTier;
  country: string;
  category: string | null;
  saleUrl: string;
  saleTitle: string | null;
  status: SaleStatus;
  discountMin: number | null;
  discountMax: number | null;
  currency: string | null;
  products: SaleProductExample[];
  lastVerifiedAt: number | null;
}

export interface SaleListResponse {
  sales: Sale[];
}

export interface BrandSummary extends Brand {
  latestStatus: SaleStatus | null;
  lastVerifiedAt: number | null;
}

export interface BrandListResponse {
  brands: BrandSummary[];
}

export interface ScanResult {
  brandId: string;
  brandName: string;
  scanStatus: 'ok' | 'partial' | 'failed';
  candidatesChecked: number;
  liveFound: number;
  error: string | null;
}

export interface ScanResponse {
  results: ScanResult[];
}

export interface AddBrandInput {
  name: string;
  luxuryTier: LuxuryTier;
  categories: SaleCategory[];
  officialDomain?: string | null;
}

export interface UpdateBrandInput {
  name?: string;
  luxuryTier?: LuxuryTier;
  categories?: SaleCategory[];
  enabled?: boolean;
}

export interface ManualSaleInput {
  brandId: string;
  url: string;
  category?: SaleCategory | null;
}

// --- Verification evidence (stored in sales.evidence_json) ---------------------

export interface SaleVerdict {
  status: SaleStatus;
  finalUrl: string;
  saleTitle: string | null;
  category: string | null;
  discountMin: number | null;
  discountMax: number | null;
  currency: string | null;
  products: SaleProductExample[];
  evidence: SaleEvidence;
  verifiedAt: number;
}

export interface SaleEvidence {
  reason: string;
  discoveryMethod: 'seeded' | 'sitemap' | 'homepage' | 'path_probe' | 'manual';
  officialDomainMatched: boolean;
  discountedProductCount: number;
  checksPassed: string[];
  notes: string[];
}

/** Discount % from a matched product's original and sale price, or null. */
export function computeDiscountPercent(
  originalMinor: number | null,
  saleMinor: number | null,
): number | null {
  if (
    originalMinor === null ||
    saleMinor === null ||
    originalMinor <= 0 ||
    saleMinor < 0 ||
    saleMinor >= originalMinor
  ) {
    return null;
  }
  return Math.round(((originalMinor - saleMinor) / originalMinor) * 100);
}
